/**
 * SendConfirmPopover — anchored confirm shown after reading the OS clipboard.
 * Pure/presentational: preview + char count + sensitive toggle + Cancel/Send.
 * SECURITY: `text` is clipboard plaintext — never logged.
 */
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { Text } from "../Text";
import styles from "./SendConfirmPopover.module.css";

export interface SendConfirmLabels {
  title: string;
  fromClipboard: (chars: number) => string;
  sensitiveToggle: string;
  cancel: string;
  send: (deviceCount: number) => string;
}

export interface SendConfirmPopoverProps {
  text: string;
  deviceCount: number;
  onConfirm: (opts: { sensitive: boolean }) => void;
  onCancel: () => void;
  labels: SendConfirmLabels;
  /** Mask the preview (public mode). Display-only — the real `text` is still sent. */
  forceMask?: boolean;
}

const MASK = "••••••••••";

export function SendConfirmPopover({
  text,
  deviceCount,
  onConfirm,
  onCancel,
  labels,
  forceMask,
}: SendConfirmPopoverProps) {
  const [sensitive, setSensitive] = useState(false);
  // Prevent a fast double-click from calling onConfirm twice.
  const [sending, setSending] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  // Captures the element focused before the dialog opened so we can restore it
  // on close (WAI-ARIA Dialog Pattern — focus returns to the invoking control).
  const prevFocusRef = useRef<HTMLElement | null>(null);

  // Auto-focus the Cancel button on mount + restore focus on close (WAI-ARIA dialog).
  // Button has no ref → query the rendered <button> for the focus target.
  useEffect(() => {
    prevFocusRef.current = document.activeElement as HTMLElement | null;
    popRef.current?.querySelector("button")?.focus();
    return () => {
      prevFocusRef.current?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      // Focus trap (WAI-ARIA Dialog Pattern) — keep Tab inside the popover.
      if (e.key !== "Tab") return;
      const pop = popRef.current;
      if (!pop) return;
      const focusable = pop.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!pop.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onCancel]
  );

  return (
    <div
      ref={popRef}
      className={styles.pop}
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
      onKeyDown={onKeyDown}
    >
      <Text size="xs" weight="semibold" tone="primary">
        {labels.title}
      </Text>
      <pre className={styles.preview}>{forceMask ? MASK : text}</pre>
      <Text size="xs" tone="tertiary">
        {labels.fromClipboard(forceMask ? MASK.length : text.length)}
      </Text>
      <Checkbox label={labels.sensitiveToggle} checked={sensitive} onChange={setSensitive} />
      <div className={styles.actions}>
        <Button variant="ghost" size="s" onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button
          variant="primary"
          size="s"
          disabled={sending}
          onClick={() => {
            setSending(true);
            onConfirm({ sensitive });
          }}
        >
          {labels.send(deviceCount)}
        </Button>
      </div>
    </div>
  );
}
